import { useMemo } from 'react';

import { getIso3 } from '@rbilabs/intl';
import { uniqBy } from 'lodash';

import { IDeliveryAddress, useUserRecentAddressesQuery } from 'generated/rbi-graphql';
import { useAuthContext } from 'state/auth';
import { convertCountryStringToIsoCountryCode2, getAppCountry } from 'utils/i18n';

const isSameCountry = (address: IDeliveryAddress) => {
  const appCountry = getAppCountry();
  const convertedAddress = convertCountryStringToIsoCountryCode2(address.country ?? '');

  return convertedAddress === appCountry;
};

const RECENT_ADDRESSES_NUMBER = 3;

export const useDeliveryAddresses = ({ skip }: { skip: boolean }) => {
  const { user } = useAuthContext();

  const { data: userRecentAddressData } = useUserRecentAddressesQuery({
    variables: {
      limit: RECENT_ADDRESSES_NUMBER,
      countryCode: getIso3({ iso2: getAppCountry() }),
    },
    skip,
  });

  const recentAddresses: IDeliveryAddress[] = useMemo(() => {
    return uniqBy(
      (userRecentAddressData?.userRecentAddresses?.addresses as IDeliveryAddress[]) ?? [],
      'addressLine1'
    );
  }, [userRecentAddressData?.userRecentAddresses?.addresses]);

  const deliveryAddresses = useMemo(() => {
    const recentAddressesAliases = new Set(recentAddresses.map(addr => addr.alias).filter(Boolean));

    return user?.details?.deliveryAddresses
      ?.filter(address => address.alias === null || !recentAddressesAliases.has(address.alias))
      .filter(isSameCountry);
  }, [user?.details?.deliveryAddresses, recentAddresses]);

  return {
    recentAddresses,
    deliveryAddresses,
  };
};
